import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Coins, HandCoins, Hourglass } from "lucide-react";

const CAMPAIGNS = ["General", "Annual Fund", "Capital Campaign", "Program Fund", "Endowment"];
const STATUS = ["Outstanding", "Partial", "Fulfilled", "Written Off"];
const EMPTY = { donor_name: "", campaign: "Annual Fund", amount: "", received_amount: "0", pledged_date: "", due_date: "" };
const bucketTone = (b) => ({ Paid: "green", Current: "blue", "1-30 days": "amber", "31-60 days": "amber", "61-90 days": "red", "90+ days": "red" }[b] || "gray");
const stTone = (s) => ({ Fulfilled: "green", Partial: "amber", Outstanding: "blue", "Written Off": "gray" }[s] || "gray");

export default function Receivables() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const canAdd = canManage || user?.role === "staff";
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/pledges").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/pledges", { ...form, amount: Number(form.amount || 0), received_amount: Number(form.received_amount || 0) });
      toast.success("Pledge recorded"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const recordPayment = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const total = (payFor.received_amount || 0) + Number(payAmt || 0);
      await api.patch(`/pledges/${payFor.id}`, { received_amount: total });
      toast.success("Payment recorded"); setPayFor(null); setPayAmt(""); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!rows) return <Loader />;
  const pledged = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const received = rows.reduce((s, r) => s + (r.received_amount || 0), 0);
  const outstanding = rows.reduce((s, r) => s + (r.balance || 0), 0);

  const cols = [
    { key: "donor_name", label: "Donor", className: "font-medium" },
    { key: "campaign", label: "Campaign", render: (r) => <StatusBadge tone="blue">{r.campaign}</StatusBadge> },
    { key: "amount", label: "Pledged", align: "right", render: (r) => fmtUSD(r.amount) },
    { key: "received_amount", label: "Received", align: "right", render: (r) => <span className="text-forest">{fmtUSD(r.received_amount)}</span> },
    { key: "balance", label: "Balance", align: "right", render: (r) => <span className="font-semibold">{fmtUSD(r.balance)}</span> },
    { key: "due_date", label: "Due", render: (r) => fmtDate(r.due_date) },
    { key: "aging_bucket", label: "Aging", render: (r) => <StatusBadge tone={bucketTone(r.aging_bucket)}>{r.aging_bucket}</StatusBadge> },
    { key: "status", label: "Status", render: (r) => <StatusBadge tone={stTone(r.status)}>{r.status}</StatusBadge> },
    ...(canManage ? [{ key: "act", label: "", render: (r) => r.balance > 0 && (
      <button onClick={() => { setPayFor(r); setPayAmt(""); }} data-testid={`pay-pledge-${r.id}`}
        className="text-xs text-forest font-medium hover:underline">Record Payment</button>) }] : []),
  ];

  return (
    <div>
      <PageHeader title="Pledges & Receivables" subtitle="Track donor pledges, payments received and outstanding balances by aging."
        action={canAdd && <AddButton onClick={() => setOpen(true)} label="Add Pledge" testid="add-pledge-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Total Pledged" value={fmtUSD(pledged)} icon={HandCoins} />
        <KpiCard label="Received to Date" value={fmtUSD(received)} icon={Coins} accent="#178070" />
        <KpiCard label="Outstanding Balance" value={fmtUSD(outstanding)} icon={Hourglass} accent="#B45309" />
      </div>
      <DataTable columns={cols} rows={rows} testid="pledges-table" empty={{ title: "No pledges recorded yet" }} />

      <Modal open={open} onOpenChange={setOpen} title="Add Pledge" onSubmit={add} busy={busy} testid="pledge-modal">
        <Field label="Donor Name" span={2}><TextInput required value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} /></Field>
        <Field label="Campaign"><SelectInput value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} options={CAMPAIGNS} /></Field>
        <Field label="Amount"><TextInput type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
        <Field label="Received So Far"><TextInput type="number" value={form.received_amount} onChange={(e) => setForm({ ...form, received_amount: e.target.value })} /></Field>
        <Field label="Pledged Date"><TextInput type="date" value={form.pledged_date} onChange={(e) => setForm({ ...form, pledged_date: e.target.value })} /></Field>
        <Field label="Due Date" span={2}><TextInput type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
      </Modal>

      <Modal open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)} title="Record Payment" onSubmit={recordPayment} busy={busy} testid="payment-modal">
        <Field label="Payment Amount" span={2}><TextInput type="number" required value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder={payFor ? `Balance: ${fmtUSD(payFor.balance)}` : ""} /></Field>
      </Modal>
    </div>
  );
}
